// src/utils/sceneProvider.ts
import { VideoCardScene } from "@akc/biz-api";
const VC_SCENE_KEY = "VIDEO_CARD_LIST_SCENE"; // 用于 sessionStorage 的键
let isInitialized = false;

/**
 * 核心规则：根据来源页面的路径名，判断应该设置什么场景值。
 * @param pathname 来源页面的路径，例如 /home, /cart/list
 * @returns {string} 计算出的场景值
 */
function determineScene(previousUrl: string): string {
  console.log(`[SceneProvider] Determining scene for previousUrl: ${previousUrl}`);

  // 如果没有路径名（例如直接访问），则为直接访问
  if (!previousUrl) {
    return VideoCardScene.videoCardOtherScene;
  }

  // 首页

  if (previousUrl.includes("/mx-shop-micro/home")) {
    return VideoCardScene.videoCardHomePage;
  }

  // 档期详情页
  if (previousUrl.includes("/mx-shop-micro/activity")) {
    return VideoCardScene.videoCardScheduleDetail;
  }

  // 购物车推荐列表
  // special: 商品详情的推荐列表要单独处理， 商品详情不仅可以跳转视频大卡，同时也是视频大卡的一个入口。
  if (previousUrl.includes("#/cart")) {
    return VideoCardScene.videoCardRecList;
  }

  // 搜索结果页
  if (previousUrl.includes("/mx-shop-micro/search") || previousUrl.includes("#/mixSearch")) {
    return VideoCardScene.videoCardSearchPage;
  }

  // 分类商品列表页
  if (previousUrl.includes("#/categoryDetail")) {
    return VideoCardScene.videoCardClassifiedProductsList;
  }

  // 品牌商品列表页
  if (previousUrl.includes("#/brandDetail")) {
    return VideoCardScene.videoCardBrandList;
  }

  // 店铺商品列表
  if (previousUrl.includes("/mx-shop-micro/decorate")) {
    return VideoCardScene.videoCardShopList;
  }

  // 会场
  if (previousUrl.includes("customPage=")) {
    return VideoCardScene.videoCardMarket;
  }

  // 如果没有匹配的规则，返回一个默认值
  return VideoCardScene.videoCardOtherScene;
}

/**
 * 初始化场景值追踪系统。
 * 这个函数应该在你的应用入口文件（如 index.ts）中被调用一次。
 */
export function initSceneProvider() {
  if (isInitialized) {
    console.warn("[SceneProvider] has already been initialized.");
    return;
  }

  // 使用一个变量来追踪上一个页面的URL
  // 初始值是 document.referrer，这完美处理了从外部链接或完整页面跳转过来的情况
  let previousUrl = document.referrer || window.location.href;

  // 定义一个更新函数，用于计算并存储场景值
  const updateVcScene = () => {
    try {
      // 如果 previousUrl 存在，就用它来生成场景值；否则认为是直接访问
      const scene = determineScene(previousUrl);
      sessionStorage.setItem(VC_SCENE_KEY, scene);
      console.log(`[SceneProvider] Scene set to "${scene}" based on previous URL: ${previousUrl}`);
    } catch (e) {
      // 如果 previousUrl 是一个无效的 URL，进行降级处理
      sessionStorage.setItem(VC_SCENE_KEY, determineScene("invalid_url"));
    }
  };

  // --- 关键部分：监听所有导航 ---

  // 1. 首次加载时，立即更新一次场景值
  updateVcScene();

  // 2. 封装 history.pushState 和 history.replaceState (处理SPA路由)
  const wrapHistoryMethod = (method: "pushState" | "replaceState") => {
    const original = history[method];
    history[method] = function (...args: Parameters<typeof history.pushState>) {
      // 在执行原始的 pushState/replaceState 之前，当前的 URL 就是即将成为的“上一个URL”
      previousUrl = window.location["href"];
      original.apply(this, args);
      // 导航发生后，立即更新场景值
      updateVcScene();
    };
  };

  wrapHistoryMethod("pushState");
  wrapHistoryMethod("replaceState");

  // 3. 监听 popstate 事件 (处理浏览器前进/后退按钮)
  window.addEventListener("popstate", () => {
    // popstate 事件触发时，URL 已经改变了。
    // 我们之前保存的 `previousUrl` 正好是这次导航的来源，所以直接用它更新场景。
    updateVcScene();
    // 更新完后，必须把当前的 URL 存下来，为下一次导航做准备。
    previousUrl = window.location["href"];
  });

  // 4. 新增：使用 requestAnimationFrame 进行轮询，作为最终的“安全网”
  // 这个方法可以捕获任何其他方法遗漏的 URL 变化。
  let lastPolledHref = window.location.href;
  const pollForUrlChange = () => {
    const currentHref = window.location.href;
    if (lastPolledHref !== currentHref) {
      console.log(`[SceneProvider Polling] Detected URL change from ${lastPolledHref}`);
      // 当轮询检测到变化时，我们用上一次轮询的 URL 作为来源
      const scene = determineScene(lastPolledHref);
      sessionStorage.setItem(VC_SCENE_KEY, scene);
      console.log(
        `[SceneProvider] Scene set to "${scene}" based on polled previous URL: ${lastPolledHref}`,
      );
      lastPolledHref = currentHref;
    }
    // 持续进行下一帧的检查
    requestAnimationFrame(pollForUrlChange);
  };

  // 启动轮询
  requestAnimationFrame(pollForUrlChange);

  // 4. 对于 `window.location.href` 的修改，我们无需特殊处理。
  // 因为它会触发完整的页面加载，届时脚本重新执行，`document.referrer` 会被正确设置。

  isInitialized = true;
  console.log("[SceneProvider] Initialized successfully.");
}

/**
 * 获取当前页面的场景值。
 * 这是给A页面或其他任何需要场景值的地方使用的函数。
 * @returns {string} 当前的场景值
 */
export function getCurrentVcScene(): string {
  return sessionStorage.getItem(VC_SCENE_KEY) || VideoCardScene.videoCardOtherScene;
}
